import React from 'react';

import axios, { type AxiosResponse } from 'axios';

import { Alert, AnchorButton, Button, Table, Text } from '@shared/components/bootstrap';

import { copyToClipboard } from '@admin/components/helpers/clipboard_utils';
import { Panel } from '@admin/components/helpers/panel';
import { Permission } from '@admin/components/helpers/permission';
import { Spinner } from '@admin/components/spinner';
import { useAxiosRequest } from '@admin/hooks';
import { useTitle } from '@admin/hooks/use_title';
import { COMMS_CATALOG_EXPORT } from '@admin/libraries/access_control_catalog.generated';

type ExportChannel = 'iterable' | 'platform';

type IterableExportRow = {
  key: string;
  name: string;
  channel: ExportChannel;
  subject: string | null;
  export_path: string;
  export_view_path: string;
  preview_path: string;
};

const EXPORT_SECTIONS: { channel: ExportChannel; title: string }[] = [
  { channel: 'iterable', title: 'Iterable' },
  { channel: 'platform', title: 'Platform' },
];

type IterableExportsIndexResponse = {
  public_www_host: string;
  admin_host: string;
  templates: IterableExportRow[];
};

type CopyState = 'idle' | 'loading' | 'copied' | 'error';

const IterableExportActions: React.FC<{ row: IterableExportRow }> = ({ row }) => {
  const [copyState, setCopyState] = React.useState<CopyState>('idle');

  React.useEffect(() => {
    if (copyState === 'copied' || copyState === 'error') {
      const timeout = window.setTimeout(() => setCopyState('idle'), 2500);
      return () => window.clearTimeout(timeout);
    }
  }, [copyState]);

  const onCopyHtml = async () => {
    setCopyState('loading');
    try {
      const { data } = await axios.get<string>(row.export_path, { responseType: 'text' });
      const success = await copyToClipboard(data);
      setCopyState(success ? 'copied' : 'error');
    } catch {
      setCopyState('error');
    }
  };

  const copyLabel =
    copyState === 'loading'
      ? 'Copying…'
      : copyState === 'copied'
        ? 'Copied'
        : copyState === 'error'
          ? 'Copy failed'
          : 'Copy HTML';

  return (
    <div className="text-right text-nowrap">
      <Button
        kind="primary"
        size="sm"
        className="mar-rgt"
        onClick={onCopyHtml}
        loading={copyState === 'loading'}
        disabled={copyState === 'loading'}
      >
        {copyLabel}
      </Button>
      <AnchorButton
        href={row.preview_path}
        kind="default"
        size="sm"
        target="_blank"
        rel="noreferrer"
        className="mar-rgt"
      >
        Sample preview
      </AnchorButton>
      <AnchorButton
        href={row.export_view_path}
        kind="default"
        size="sm"
        target="_blank"
        rel="noreferrer"
        className="mar-rgt"
      >
        View export
      </AnchorButton>
      <AnchorButton href={row.export_path} kind="link" size="sm">
        Download
      </AnchorButton>
    </div>
  );
};

const useIterableExportsRequest = () => {
  const [request, setRequest] = React.useState<Promise<AxiosResponse<IterableExportsIndexResponse>> | undefined>(
    undefined,
  );

  React.useEffect(() => {
    setRequest(axios.get('/marketing/email_exports.json'));
  }, []);

  return useAxiosRequest(request);
};

const IterableExportTable: React.FC<{ rows: IterableExportRow[] }> = ({ rows }) => (
  <Table>
    <thead>
      <tr>
        <th>Template</th>
        <th>Catalog key</th>
        <th>Subject</th>
        <th />
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={row.key}>
          <td>{row.name}</td>
          <td>
            <code>{row.key}</code>
          </td>
          <td>{row.subject ?? '—'}</td>
          <td>
            <IterableExportActions row={row} />
          </td>
        </tr>
      ))}
    </tbody>
  </Table>
);

const IterableExportsPanel: React.FC = () => {
  const { loading, response, error } = useIterableExportsRequest();
  const payload = response?.data;
  const rows = payload?.templates ?? [];
  const wwwHost = payload?.public_www_host;
  const adminHost = payload?.admin_host;

  const rowsByChannel = React.useMemo(() => {
    const grouped: Record<ExportChannel, IterableExportRow[]> = { iterable: [], platform: [] };
    for (const row of rows) {
      const channel = row.channel === 'platform' ? 'platform' : 'iterable';
      grouped[channel].push(row);
    }
    return grouped;
  }, [rows]);

  return (
    <Panel>
      <Panel.Body>
        <h1>Email Exports</h1>
        <Text tag="p" style="muted">
          Iterable rows include Handlebars merge tags for Iterable campaigns. Platform (Teal) rows use sample portal
          links but the same Premailer-inlined HTML (safe to paste into Litmus or Iterable for layout QA). Sample
          preview uses mock data; View export is the exact inlined file.
          {wwwHost && adminHost ? (
            <>
              {' '}
              CTA links use this environment&apos;s public www host ({wwwHost}). Mailer images and stars use the admin
              host ({adminHost}).
            </>
          ) : null}
        </Text>
        {loading ? (
          <Spinner />
        ) : error ? (
          <Alert style="danger">Could not load exportable templates.</Alert>
        ) : (
          EXPORT_SECTIONS.map((section) => {
            const sectionRows = rowsByChannel[section.channel];
            if (sectionRows.length === 0) {
              return null;
            }

            return (
              <section key={section.channel} className="mar-btm-xl">
                <h2 className="h4 mar-btm">{section.title}</h2>
                <IterableExportTable rows={sectionRows} />
              </section>
            );
          })
        )}
      </Panel.Body>
    </Panel>
  );
};

export const IterableExportsList: React.FC = () => {
  useTitle('Email Exports');

  return (
    <Permission resource={COMMS_CATALOG_EXPORT.resource} action={COMMS_CATALOG_EXPORT.actions.read}>
      <IterableExportsPanel />
    </Permission>
  );
};
